import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import api from "../../services/api";
import { formatRelativeTime } from "../../utils/formatters";
import AttentionHistoryModal from "./AttentionHistoryModal";
import TasksTeamView from "./TasksTeamView";
import styles from "./TasksPage.module.css";

const RISK_LABELS = {
  sla_breach: "SLA breached",
  sla_warning: "SLA at risk",
  refill_waiting: "Refill awaiting review",
  refund_failed: "Refund failed",
  order_denied: "Order denied",
};

// The two halves of this page, so the Support workspace can mount them as its
// "My Work" / "Needs Attention" tabs instead of duplicating them. Omitting the
// prop renders both — /admin/tasks keeps working standalone as the rollback.
export const TASK_SECTIONS = {
  MY_WORK: "myWork",
  ATTENTION: "attention",
};

const ALL_SECTIONS = [TASK_SECTIONS.MY_WORK, TASK_SECTIONS.ATTENTION];

const formatValue = (value) =>
  value ? value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "";

const formatMinutes = (mins) => {
  if (mins === null || mins === undefined) return "\u2014";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "\u2014";

const TasksPage = ({ sections = ALL_SECTIONS }) => {
  const showMyWork = sections.includes(TASK_SECTIONS.MY_WORK);
  const showAttention = sections.includes(TASK_SECTIONS.ATTENTION);
  // Only the standalone route shows both halves, so that is the only place the
  // page title belongs — embedded in a Support tab it would be a second <h1>.
  const isStandalone = showMyWork && showAttention;
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { toast } = useToast();
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [risks, setRisks] = useState(null);
  const [risksLoading, setRisksLoading] = useState(true);
  const [risksError, setRisksError] = useState(null);
  // Ticket whose attention history modal is open (null = closed).
  const [historyTicket, setHistoryTicket] = useState(null);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const response = await api.get("/admin/tasks/my-dashboard");
      setDashboard(response);
      setDashboardError(null);
    } catch (error) {
      console.error("Failed to load tasks dashboard:", error);
      setDashboardError("Failed to load your dashboard");
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadRisks = useCallback(async () => {
    setRisksLoading(true);
    try {
      const response = await api.get("/admin/tasks/order-risks");
      setRisks(response);
      setRisksError(null);
    } catch (error) {
      console.error("Failed to load order risks:", error);
      setRisksError("Failed to load order risks");
    } finally {
      setRisksLoading(false);
    }
  }, []);

  useEffect(() => {
    // Both halves read the dashboard (My Work its tickets/metrics, Needs
    // Attention the overdue subset + overview); only the latter needs risks.
    loadDashboard();
    if (showAttention) loadRisks();
  }, [loadDashboard, loadRisks, showAttention]);

  // Per-ticket in-flight guard: a rapid double-click fires exactly ONE request.
  const savingHandledRef = useRef(new Set());
  const canMarkHandled = hasPermission("tasks.mark_handled");
  const canUndoHandled = hasPermission("tasks.undo_handled");
  const canViewHistory = hasPermission("tasks.history_view");

  const patchTicketAttention = useCallback((ticketId, patch) => {
    setDashboard((prev) =>
      prev
        ? {
            ...prev,
            activeTickets: (prev.activeTickets || []).map((t) =>
              t.id === ticketId ? { ...t, ...patch } : t
            ),
          }
        : prev
    );
  }, []);

  // Optimistic flip; the server response is authoritative for handledAt /
  // visibleUntil and a failure reverts the row + toasts (mirrors the
  // AbandonedIntakesPage reached-out checkbox). Handled rows STAY in the list
  // during their 24h window — the server drops them once the window closes.
  const toggleHandled = useCallback(
    async (ticket) => {
      const markingHandled = !ticket.isHandled;
      if (markingHandled ? !canMarkHandled : !canUndoHandled) return;
      if (savingHandledRef.current.has(ticket.id)) return;
      savingHandledRef.current.add(ticket.id);

      const prevAttention = {
        isHandled: ticket.isHandled,
        handledAt: ticket.handledAt,
        handledBy: ticket.handledBy,
        handledByName: ticket.handledByName,
        visibleUntil: ticket.visibleUntil,
      };
      patchTicketAttention(ticket.id, {
        isHandled: markingHandled,
        handledByName: markingHandled ? user?.profile?.fullName ?? null : null,
      });
      try {
        const data = await api.post(
          `/admin/tasks/tickets/${ticket.id}/attention/${markingHandled ? "handled" : "undo"}`
        );
        const attention = data?.attention ?? {};
        patchTicketAttention(ticket.id, {
          isHandled: attention.isHandled ?? markingHandled,
          handledAt: attention.handledAt ?? null,
          handledBy: attention.handledBy ?? null,
          handledByName: attention.isHandled
            ? user?.profile?.fullName ?? prevAttention.handledByName ?? null
            : null,
          visibleUntil: attention.visibleUntil ?? null,
        });
        toast.success(markingHandled ? "Marked as handled" : "Moved back to active");
      } catch (err) {
        patchTicketAttention(ticket.id, prevAttention);
        toast.error(err.message || "Failed to update handled status");
      } finally {
        savingHandledRef.current.delete(ticket.id);
      }
    },
    [canMarkHandled, canUndoHandled, patchTicketAttention, toast, user]
  );

  const getStatusClass = (status) =>
    ({
      open: styles.statusOpen,
      in_progress: styles.statusInProgress,
      resolved: styles.statusResolved,
      closed: styles.statusClosed,
    })[status] || styles.statusOpen;

  const getPriorityClass = (priority) =>
    ({
      low: styles.priorityLow,
      normal: styles.priorityNormal,
      medium: styles.priorityNormal,
      high: styles.priorityHigh,
      urgent: styles.priorityUrgent,
    })[priority] || styles.priorityNormal;

  const openTicket = (ticket) =>
    navigate(
      `/admin/support-tickets?ticket=${encodeURIComponent(ticket.ticketNumber)}`
    );

  const metrics = dashboard?.myMetrics;
  const overview = dashboard?.overview;
  const activeTickets = dashboard?.activeTickets || [];
  const overdueTickets = activeTickets.filter((t) => t.overdue);
  const riskItems = risks?.risks || [];

  const kpis = metrics
    ? [
        { label: "In Progress", value: metrics.inProgressCount },
        { label: "Closed Today", value: metrics.closedToday },
        {
          label: "High Priority Open",
          value:
            (overview?.totals?.highPriorityOpen || 0) +
            (overview?.totals?.urgentOpen || 0),
          alert: true,
        },
        {
          label: "Avg Resolution",
          value: formatMinutes(metrics.avgResolutionMinutes),
        },
      ]
    : [];

  const renderError = (message, retry) => (
    <div className={styles.errorBox}>
      {message}{" "}
      <button className={styles.retryBtn} onClick={retry}>
        Retry
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      {isStandalone && (
        <div className={styles.header}>
          <div>
            <h1>Tasks</h1>
            <p>Your active tickets, metrics, and items needing attention</p>
          </div>
        </div>
      )}

      {showMyWork && (dashboardLoading ? (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      ) : dashboardError ? (
        renderError(dashboardError, loadDashboard)
      ) : (
        <>
          {/* KPI Cards */}
          <div className={styles.kpiGrid}>
            {kpis.map((kpi) => (
              <div key={kpi.label} className={styles.kpiCard}>
                <span
                  className={`${styles.kpiValue} ${kpi.alert && kpi.value > 0 ? styles.kpiAlert : ""}`}
                >
                  {kpi.value}
                </span>
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
            ))}
          </div>

          {/* My Active Tickets */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>My Active Tickets</h2>
            <div className={styles.tableCard}>
              {activeTickets.length === 0 ? (
                <div className={styles.empty}>
                  No active tickets — nice work
                </div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Subject</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Last Interaction</th>
                      <th>In Status</th>
                      <th>Aging</th>
                      <th>Linked</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className={styles.ticketNumber}>
                          #{ticket.ticketNumber}
                        </td>
                        <td>
                          <div className={styles.userCell}>
                            <span className={styles.userName}>
                              {ticket.customerName}
                            </span>
                            <span className={styles.userEmail}>
                              {ticket.customerEmail}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.subjectCell}>
                            <span className={styles.subject}>
                              {ticket.subject}
                            </span>
                            <span className={styles.category}>
                              {formatValue(ticket.category)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`${styles.priorityBadge} ${getPriorityClass(ticket.priority)}`}
                          >
                            {formatValue(ticket.priority)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${getStatusClass(ticket.status)}`}
                          >
                            {formatValue(ticket.status)}
                          </span>
                        </td>
                        <td className={styles.dateCell}>
                          {formatDateTime(ticket.createdAt)}
                        </td>
                        <td className={styles.dateCell}>
                          {formatDateTime(ticket.lastInteractionAt)}
                        </td>
                        <td className={styles.dateCell}>
                          {formatMinutes(ticket.timeInCurrentStatusMinutes)}
                        </td>
                        <td>
                          <span
                            className={`${styles.agingBadge} ${ticket.overdue ? styles.agingOverdue : styles.agingOk}`}
                          >
                            {ticket.overdue ? "Overdue" : "On track"}
                          </span>
                        </td>
                        <td>
                          <div className={styles.linkedCell}>
                            {ticket.relatedOrderId && (
                              <Link
                                to={`/admin/orders/${ticket.relatedOrderId}`}
                                className={styles.linkedTag}
                              >
                                Order
                              </Link>
                            )}
                            {ticket.relatedRefillRequestId && (
                              <span className={styles.linkedTag}>Refill</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => openTicket(ticket)}
                          >
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ))}

      {/* Tickets Needing Attention */}
      {showAttention && hasPermission("tasks_needing_attention.view") && <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Tickets Needing Attention</h2>
        {risksLoading || dashboardLoading ? (
          <div className={styles.loading}>
            <LoadingSpinner size="small" />
          </div>
        ) : risksError ? (
          renderError(risksError, loadRisks)
        ) : overdueTickets.length === 0 && riskItems.length === 0 ? (
          <div className={styles.emptyCard}>
            Nothing needs attention right now
          </div>
        ) : (
          <div className={styles.attentionList}>
            {overdueTickets.map((ticket) => (
              <div
                key={`ticket-${ticket.id}`}
                className={`${styles.attentionItem} ${styles.attentionRed} ${ticket.isHandled ? styles.attentionHandled : ""}`}
              >
                <button
                  type="button"
                  className={styles.attentionMain}
                  onClick={() => openTicket(ticket)}
                >
                  <span className={`${styles.severityDot} ${styles.dotRed}`} />
                  <span className={styles.attentionText}>
                    <span className={styles.attentionTopLine}>
                      <span className={styles.attentionLabel}>Ticket overdue</span>
                      <span className={styles.attentionDetail}>
                        #{ticket.ticketNumber} · {ticket.subject}
                      </span>
                    </span>
                    {ticket.isHandled && ticket.handledAt && (
                      <span
                        className={styles.attentionSubtext}
                        title={`Handled ${formatDateTime(ticket.handledAt)}${ticket.visibleUntil ? ` · auto-archives at ${formatDateTime(ticket.visibleUntil)}` : ""}`}
                      >
                        Handled {formatRelativeTime(ticket.handledAt)}
                        {ticket.handledByName ? ` by ${ticket.handledByName}` : ""}
                      </span>
                    )}
                  </span>
                </button>
                {(canMarkHandled || canViewHistory) && (
                  <div className={styles.attentionCheck}>
                    {canViewHistory && (
                      <button
                        type="button"
                        className={styles.historyBtn}
                        onClick={() => setHistoryTicket(ticket)}
                        aria-label={`View attention history for ticket #${ticket.ticketNumber}`}
                        title="Attention history"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M3 3v5h5" />
                          <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                          <path d="M12 7v5l4 2" />
                        </svg>
                      </button>
                    )}
                    {canMarkHandled && (
                      <>
                        <input
                          type="checkbox"
                          className={styles.attentionCheckbox}
                          checked={Boolean(ticket.isHandled)}
                          disabled={ticket.isHandled && !canUndoHandled}
                          onChange={() => toggleHandled(ticket)}
                          aria-label={`Ticket #${ticket.ticketNumber} handled`}
                          title={
                            ticket.isHandled
                              ? canUndoHandled
                                ? "Move back to active"
                                : "Requires undo permission"
                              : "Mark as handled"
                          }
                        />
                        {ticket.isHandled && canUndoHandled && (
                          <button
                            type="button"
                            className={styles.undoBtn}
                            onClick={() => toggleHandled(ticket)}
                          >
                            Undo
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            {riskItems.map((risk) => (
              <Link
                key={`${risk.type}-${risk.orderId}`}
                to={risk.link}
                className={`${styles.attentionItem} ${risk.severity === "red" ? styles.attentionRed : styles.attentionOrange}`}
              >
                <span
                  className={`${styles.severityDot} ${risk.severity === "red" ? styles.dotRed : styles.dotOrange}`}
                />
                <span className={styles.attentionLabel}>
                  {RISK_LABELS[risk.type] || formatValue(risk.type)}
                </span>
                <span className={styles.attentionDetail}>
                  {risk.orderNumber}
                  {risk.stage ? ` · ${formatValue(risk.stage)} stage` : ""}
                  {risk.orderStatus ? ` · ${formatValue(risk.orderStatus)}` : ""}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>}

      {/* Customer Service Overview */}
      {showAttention && hasPermission("tasks_overview.view") && overview && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Customer Service Overview</h2>
          <div className={styles.statStrip}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{overview.createdToday}</span>
              <span className={styles.statLabel}>Created Today</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{overview.closedToday}</span>
              <span className={styles.statLabel}>Closed Today</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{overview.reopenedToday}</span>
              <span className={styles.statLabel}>Reopened Today</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>
                {formatMinutes(overview.avgFirstResponseMinutes)}
              </span>
              <span className={styles.statLabel}>Avg First Response</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>
                {formatMinutes(overview.avgResolutionMinutes)}
              </span>
              <span className={styles.statLabel}>Avg Resolution</span>
            </div>
            {overview.oldestUnresolved && (
              <div className={styles.statItem}>
                <span className={styles.statValue}>
                  {formatMinutes(overview.oldestUnresolved.ageHours * 60)}
                </span>
                <span className={styles.statLabel}>
                  Oldest Unresolved (#{overview.oldestUnresolved.ticketNumber})
                </span>
              </div>
            )}
          </div>
          <div className={styles.breakdownGrid}>
            {[
              { title: "By Status", data: overview.byStatus },
              { title: "By Priority", data: overview.byPriority },
              { title: "By Category", data: overview.byCategory },
            ].map(({ title, data }) => (
              <div key={title} className={styles.breakdownCard}>
                <span className={styles.breakdownTitle}>{title}</span>
                {Object.entries(data || {})
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, count]) => (
                    <div key={key} className={styles.breakdownRow}>
                      <span>{formatValue(key)}</span>
                      <span className={styles.breakdownCount}>{count}</span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team view (Super Admin) */}
      {showAttention && hasPermission("tasks.view_all") && (
        <TasksTeamView
          risks={riskItems}
          risksLoading={risksLoading}
          risksError={risksError}
          onRetryRisks={loadRisks}
        />
      )}

      {showAttention && canViewHistory && (
        <AttentionHistoryModal
          ticket={historyTicket}
          onClose={() => setHistoryTicket(null)}
        />
      )}
    </div>
  );
};

export default TasksPage;
